import './fristen.css';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtDate, initials } from '../../lib/format';
import { daysUntil, inDays } from '../../lib/money';
import { useApi } from '../../lib/useApi';
import { letterPath } from '../briefe/labels';
import { FilingDialog } from './FilingDialog';
import type { FristItem, FristenResponse } from './types';

// Karte „Nächste Fristen“ auf der Übersicht (wie in der Demo): nächste UStVA meiner FiBu-Mandanten und Fristen aus
// offenen Briefen, überfällige und die nächsten 45 Tage, knappste zuerst. Rot ab einer Woche.
export const useFristen = () => useApi<FristenResponse>('/api/kanzlei/fristen', { live: ['fristen', 'briefe', 'clients'] });

export function FristenKpi() {
  const res = useFristen();
  const d = res.data;
  if (!d) return null;
  const week = d.items.filter((i) => daysUntil(i.date, d.today) <= 7).length;
  return (
    <div className="card">
      <div className="kl">{d.fibu ? 'Fristen in 7 Tagen' : 'Brief-Fristen in 7 Tagen'}</div>
      <div className="kv num" style={week ? { color: 'var(--bad)' } : undefined}>
        {week}
      </div>
      <div className="ks">noch offen</div>
    </div>
  );
}

export function FristenCard() {
  const navigate = useNavigate();
  const res = useFristen();
  const [filing, setFiling] = useState<FristItem | null>(null);
  const d = res.data;
  if (!d) return null;

  return (
    <div className="card fristen-k">
      <div className="ch">
        <h3>Nächste Fristen</h3>
        <span>{d.items.length ? 'noch offen' : d.fibu ? 'alles im Plan' : 'nur Briefe'}</span>
      </div>
      {d.items.length ? (
        d.items.map((f) => {
          const soon = daysUntil(f.date, d.today) <= 7;
          return (
            <div className="task" key={`${f.kind}-${f.id}`}>
              <span className="av" aria-hidden="true">
                {f.kind === 'brief' ? '✉' : initials(f.clientName)}
              </span>
              <div className="tx">
                <b>{f.title}</b> · {f.clientName}
                <small>
                  Frist {fmtDate(f.date)}
                  {f.kind === 'ustva' && !f.over && ' · der Zeitraum läuft noch'}
                  {d.admin && f.who !== undefined && (
                    <span className={f.who ? undefined : 'who-open'}>
                      {' '}
                      · {f.kind === 'ustva' ? 'FiBu' : 'zuständig'}: {f.who ?? 'offen, bitte zuweisen'}
                    </span>
                  )}
                </small>
              </div>
              <span className={`pill ${soon ? 'p-bad' : 'p-neu'}`}>{inDays(f.date, d.today)}</span>
              {f.kind === 'brief' ? (
                <button
                  type="button"
                  className={`btn sm${soon ? ' dark' : ''}`}
                  onClick={() => navigate(letterPath(f.clientId, f.id))}
                >
                  Brief ansehen
                </button>
              ) : (
                f.over && (
                  <button type="button" className={`btn sm${soon ? ' dark' : ''}`} onClick={() => setFiling(f)}>
                    Übermittelt eintragen
                  </button>
                )
              )}
            </div>
          );
        })
      ) : (
        <p className="note">{d.fibu ? 'Alles übermittelt. Keine offenen Fristen.' : 'Keine offenen Brief-Fristen.'}</p>
      )}
      {!d.fibu && <p className="note fk-hint">Hier stehen Fristen aus Briefen Ihrer Mandanten. UStVA-Fristen führt die FiBu.</p>}
      {filing?.period && (
        <FilingDialog
          clientId={filing.clientId}
          clientName={filing.clientName}
          period={{ ...filing.period, title: filing.title, deadline: filing.date }}
          onClose={() => {
            setFiling(null);
            void res.reload();
          }}
        />
      )}
    </div>
  );
}
